import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Location } from 'src/app/models/location.model';
import { Timeslot } from 'src/app/models/timeslot.model';
import { ContactInformationFormData } from 'src/app/models/contact-information-form-data.model';
import { Time } from 'src/app/shared/utils/time.util';
import { BookedAppointment } from 'src/app/models/booked-appointment.model';
import { StoreKeys } from 'src/app/enums/store-keys.enum';
import { CancellationDetails } from 'src/app/models/cancellation-details.model';
import { HomeAddress } from 'src/app/models/home-address.model';

@Injectable({
  providedIn: 'root'
})
export class CommonStoreService {
  private readonly selectedAddressSubj$ = new BehaviorSubject<HomeAddress | null>(this.getFromStorage(StoreKeys.SelectedAddress));
  private readonly selectedLocationSubj$ = new BehaviorSubject<Location | null>(this.getFromStorage(StoreKeys.SelectedLocation));
  private readonly selectedTimeslotSubj$ = new BehaviorSubject<Timeslot | null>(
    this.restoreTimeslot(this.getFromStorage<Timeslot>(StoreKeys.SelectedTimeslot))
  );
  private readonly contactInfoSubj$ = new BehaviorSubject<ContactInformationFormData | null>(
    this.restoreContactInfo(this.getFromStorage<ContactInformationFormData>(StoreKeys.ContactInfo))
  );
  private readonly bookedAppointmentSubj$ = new BehaviorSubject<BookedAppointment | null>(
    this.restoreBookedAppointment(this.getFromStorage(StoreKeys.BookedAppointment))
  );
  private readonly cancellationDetailsSubj$ = new BehaviorSubject<CancellationDetails | null>(
    this.getFromStorage(StoreKeys.CancellationDetails)
  );

  public readonly selectedAddress$ = this.selectedAddressSubj$.asObservable();
  public readonly selectedLocation$ = this.selectedLocationSubj$.asObservable();
  public readonly selectedTimeslot$ = this.selectedTimeslotSubj$.asObservable();
  public readonly contactInfo$ = this.contactInfoSubj$.asObservable();
  public readonly bookedAppointment$ = this.bookedAppointmentSubj$.asObservable();
  public readonly cancellationDetails$ = this.cancellationDetailsSubj$.asObservable();

  public getSelectedAddressSnapshot(): HomeAddress | null {
    return this.selectedAddressSubj$.value;
  }

  public getSelectedLocationSnapshot(): Location | null {
    return this.selectedLocationSubj$.value;
  }

  public getSelectedTimeslotSnapshot(): Timeslot | null {
    return this.selectedTimeslotSubj$.value;
  }

  public getContactInfoSnapshot(): ContactInformationFormData | null {
    return this.contactInfoSubj$.value;
  }

  public getBookedAppointmentSnapshot(): BookedAppointment | null {
    return this.bookedAppointmentSubj$.value;
  }

  public getCancellationDetailsSnapshot(): CancellationDetails | null {
    return this.cancellationDetailsSubj$.value;
  }

  public setSelectedAddress(address: HomeAddress | null): void {
    sessionStorage.setItem(StoreKeys.SelectedAddress, JSON.stringify(address));
    this.selectedAddressSubj$.next(address);
  }

  public setSelectedLocation(location: Location | null): void {
    sessionStorage.setItem(StoreKeys.SelectedLocation, JSON.stringify(location));
    this.selectedLocationSubj$.next(location);
  }

  public setSelectedTimeslot(timeslot: Timeslot | null): void {
    sessionStorage.setItem(StoreKeys.SelectedTimeslot, JSON.stringify(timeslot));
    this.selectedTimeslotSubj$.next(timeslot);
  }

  public setContactInfo(contactInfo: ContactInformationFormData | null): void {
    sessionStorage.setItem(StoreKeys.ContactInfo, JSON.stringify(contactInfo));
    this.contactInfoSubj$.next(contactInfo);
  }

  public setBookedAppointment(appointmentInfo: BookedAppointment | null): void {
    sessionStorage.setItem(StoreKeys.BookedAppointment, JSON.stringify(appointmentInfo));
    this.bookedAppointmentSubj$.next(appointmentInfo);
  }

  public setCancellationDetails(cancellationDetails: CancellationDetails | null): void {
    sessionStorage.setItem(StoreKeys.CancellationDetails, JSON.stringify(cancellationDetails));
    this.cancellationDetailsSubj$.next(cancellationDetails);
  }

  public reset(): void {
    this.resetSelectedAddress();
    this.resetSelectedLocation();
    this.resetSelectedTimeslot();
    this.resetContactInfo();
    this.resetBookedAppointment();
    this.resetCancellationDetails();
  }

  public resetSelectedAddress(): void {
    sessionStorage.removeItem(StoreKeys.SelectedAddress);
    this.setSelectedAddress(null);
  }

  public resetSelectedLocation(): void {
    sessionStorage.removeItem(StoreKeys.SelectedLocation);
    this.setSelectedLocation(null);
  }

  public resetSelectedTimeslot(): void {
    sessionStorage.removeItem(StoreKeys.SelectedTimeslot);
    this.setSelectedTimeslot(null);
  }

  public resetContactInfo(): void {
    sessionStorage.removeItem(StoreKeys.ContactInfo);
    this.setContactInfo(null);
  }
  public resetBookedAppointment(): void {
    sessionStorage.removeItem(StoreKeys.BookedAppointment);
    this.setBookedAppointment(null);
  }

  public resetCancellationDetails(): void {
    sessionStorage.removeItem(StoreKeys.CancellationDetails);
    this.setCancellationDetails(null);
  }

  private getFromStorage<T>(key: string): T | null {
    const raw = sessionStorage.getItem(key);
    if (raw) {
      try {
        return JSON.parse(raw) as T;
      } catch {
        return null;
      }
    }
    return null;
  }

  private restoreTimeslot(timeslot: Timeslot | null): Timeslot | null {
    if (timeslot) {
      return {
        ...timeslot,
        date: new Date(timeslot.date),
        time: new Time(timeslot.time),
        endTime: new Time(timeslot.endTime)
      };
    }
    return null;
  }

  private restoreContactInfo(formData: ContactInformationFormData | null): ContactInformationFormData | null {
    if (formData) {
      return {
        ...formData,
        dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth) : null
      };
    }
    return null;
  }

  private restoreBookedAppointment(appointment: BookedAppointment | null): BookedAppointment | null {
    if (appointment) {
      const timeslot = this.restoreTimeslot(appointment.timeslot);

      if (!timeslot) {
        return null;
      }

      return {
        ...appointment,
        createdAt: new Date(appointment.createdAt),
        timeslot,
        appointmentData: this.restoreContactInfo(appointment.appointmentData)!
      };
    }

    return null;
  }
}
